import { APP_TIME_ZONE, toDate, type DateInput } from '../format.js';

/**
 * Whether a follow-up is late, due today, or still ahead — decided the same way the SERVER
 * decides it.
 *
 * This is a deliberate duplicate of the API's `shopDayBounds`, and it has to be. The list gets a
 * date and has to colour the row now, without a round trip; the API has to answer "which tasks
 * are overdue" without trusting a browser clock. Both must draw midnight in exactly the same
 * place, or a row shows red inside a filter that says it is not due — so both draw it at the
 * start of the day in `APP_TIME_ZONE`, never at "now" and never in the browser's own zone.
 *
 * `overdue` therefore means "before today began", not "in the past". A task due at 4pm today is
 * still due today at 5pm — a shop that gets shouted at by its own screen an hour after lunch
 * stops reading the colours at all.
 */
export type FollowUpState = 'none' | 'overdue' | 'today' | 'upcoming';

/** Midnight at the start of the shop's current day, as an instant. */
export function shopDayStart(now: DateInput = Date.now()): Date {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: APP_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(toDate(now));
  const get = (type: string): string => parts.find((part) => part.type === type)?.value ?? '00';
  // IST is UTC+5:30 year-round; India has no daylight saving.
  return new Date(`${get('year')}-${get('month')}-${get('day')}T00:00:00+05:30`);
}

/**
 * `YYYY-MM-DD`, `days` from now — the format `<input type="date">` requires.
 *
 * Lives here rather than beside either dialog that wants it, because both the log-contact form
 * and the prompt that follows a stage change offer the same intervals, and two copies is how
 * "tomorrow" comes to mean two different days on two screens.
 */
export function dateInDays(days: number): string {
  const date = new Date();
  date.setDate(date.getDate() + days);
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

/**
 * A `YYYY-MM-DD` from a date input, as the instant the API stores.
 *
 * 9am rather than midnight: a follow-up is a thing somebody does during a working day, and a date
 * pinned to midnight reads as due "yesterday" the moment the shop opens.
 */
export function followUpInstant(date: string): string {
  return new Date(`${date}T09:00:00`).toISOString();
}

export function followUpState(
  nextFollowUpAt: string | null,
  now: DateInput = Date.now(),
): FollowUpState {
  if (!nextFollowUpAt) return 'none';
  const due = toDate(nextFollowUpAt);
  if (Number.isNaN(due.getTime())) return 'none';

  const start = shopDayStart(now);
  if (due.getTime() < start.getTime()) return 'overdue';
  const end = start.getTime() + 24 * 60 * 60 * 1000;
  return due.getTime() < end ? 'today' : 'upcoming';
}
